import { fetchJsonWithAuth } from '../shared/fetch-json.util';
import { getEnvVar } from '../utils/getEnvVar';
import {
  NO_SBF,
  SMALL_BUSINESS_FORM,
  STRATEGY_STATUS_ACTIVE,
  STRATEGY_STATUS_DRAFT
} from '../shared/AppConstants';
import {
  addPropAndValue,
  formatAmountWithCommas,
  ignoreCaseSearch,
  isScopeAnyGlobalType
} from '../shared/UtilFunctions';
import { getFormattedUserNameWithId } from '../shared/UserLogin';
import { gridSortedRowEntriesSelector } from '@mui/x-data-grid-pro';

/*
  input format  --> [{"commodity":"D01","major":"01","minor":"01","segment":"20"}, ...]

  output format --> [{"commodity":"D01","major":"01","minors":[{"code":"01","segments":["20"]}, ...]}, ...]
*/
export function getNewMGCCriteria(selectedMGCs) {
  let map = new Map();
  let commMajorKey;
  let searchObj;
  let minorObj;

  selectedMGCs &&
    selectedMGCs.length > 0 &&
    selectedMGCs.forEach((mgc) => {
      commMajorKey = mgc.commodity + mgc.major;
      searchObj = map.get(commMajorKey);

      if (!searchObj) {
        searchObj = {
          commodity: mgc.commodity,
          major: mgc.major,
          minors: mgc.major && []
        };
        map.set(commMajorKey, searchObj);
      }
      if (mgc.minor) {
        // Minor present
        minorObj = searchObj.minors.find((elem) => elem.code === mgc.minor);

        if (!minorObj) {
          minorObj = { code: mgc.minor, segments: [] };
          // Add to list
          searchObj.minors.push(minorObj);
        }
        // Segment
        mgc.segment && minorObj.segments.push(mgc.segment);
      }
    });

  return Array.from(map.values());
}

export async function checkStrategyDuplicateCombination(
  mgcs,
  statusList,
  newSelectedScope,
  newSelectedUnitGroups,
  newSelectedUnits,
  currentStartegyNumber,
  commonProps
) {
  let { setStatusErrorMsg, incrementAPIInProgress, decrementAPIInProgress } =
    commonProps;
  let searchObj = {};

  addPropAndValue(searchObj, 'embed', 'decorate');
  addPropAndValue(searchObj, 'status', statusList);
  addPropAndValue(searchObj, 'mgcs', mgcs);

  if (newSelectedScope) {
    let scopeObject = {};

    addPropAndValue(scopeObject, 'level', newSelectedScope);

    if (!isScopeAnyGlobalType(newSelectedScope)) {
      if (newSelectedUnitGroups)
        addPropAndValue(scopeObject, 'unitGroups', newSelectedUnitGroups);
      if (newSelectedUnits)
        addPropAndValue(scopeObject, 'units', newSelectedUnits);
    }
    addPropAndValue(searchObj, 'scope', scopeObject);
  }

  let apiUrl = getEnvVar('VITE_GSS_API_URL') + '/searchMinimal';
  let outList = [];

  incrementAPIInProgress();

  await fetchJsonWithAuth(apiUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json;charset=utf-8'
    },
    body: JSON.stringify(searchObj)
  })
    .then((response) => {
      response.json.forEach((item) => {
        // Filter the Current Strategy
        if (item.strategyId !== currentStartegyNumber) outList.push(item);
      });
    })
    .catch((error) =>
      setStatusErrorMsg(
        "Couldn't retrieve the Strategy info. Please try again.",
        error
      )
    )
    .finally(() => decrementAPIInProgress());
  return outList;
}

export function findStrategyOwnerApprovers(
  setStrategyOwnerApproverList,
  incrementAPIInProgress,
  decrementAPIInProgress,
  setStatusErrorMsg
) {
  let searchObj = { status: [STRATEGY_STATUS_DRAFT, STRATEGY_STATUS_ACTIVE] };
  let apiUrl = getEnvVar('VITE_GSS_API_URL') + '/searchMinimal';

  incrementAPIInProgress();
  fetchJsonWithAuth(apiUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json;charset=utf-8'
    },
    body: JSON.stringify(searchObj)
  })
    .then((response) => {
      const userSet = new Set();

      response.json.forEach((item) => {
        userSet.add(item.ownerId);
        userSet.add(item.approverId);
      });
      setStrategyOwnerApproverList(Array.from(userSet));
    })
    .catch((error) =>
      setStatusErrorMsg(
        "Couldn't retrieve the Strategy info. Please try again.",
        error
      )
    )
    .finally(() => decrementAPIInProgress());
}

function getSortedUnitGroupOrUnitScope(strategyInformation) {
  let list = [];

  strategyInformation.decorated.scope.unitGroups &&
    strategyInformation.decorated.scope.unitGroups.forEach((record) =>
      list.push(record.name)
    );
  strategyInformation.decorated.scope.units &&
    strategyInformation.decorated.scope.units.forEach((record) =>
      list.push(record.name)
    );

  return list.sort();
}

function formatSpendValue(spend, index, key) {
  return formatAmountWithCommas(spend?.[index]?.values?.[key] ?? 0, true);
}

function getSpendTotal(spend, index) {
  const values = spend?.[index]?.values;

  return (values?.['this strategy'] ?? 0) + (values?.['other strategy'] ?? 0);
}

function prepareSupplierExportRow(supplier) {
  return {
    number: supplier.supplierNumber ?? supplier.number,
    name: supplier.supplierName ?? supplier.name,
    status: supplier.status,
    invoice: Math.round(supplier.invoiceSpend ?? supplier.r12Spend ?? 0),
    forecast: Math.round(supplier.forecastSpend ?? supplier.r12Forecast ?? 0),
    forecastSpendPercentage:
      Math.round(supplier.forecastSpendPercentage ?? 0) + '%',
    risk: supplier.riskRating,
    sdi: supplier.sdis && supplier.sdis.join(', '),
    aeRating: supplier.aeRating,
    sroSbmName: supplier.sroSbmName,
    comments: supplier.comments?.replaceAll(/(<([^</>]+)>)/gi, ' '),
    unitGroups: supplier.unitGroupsSpend ?? []
  };
}

function getSortedSupplierRows(apiRef, fallbackSuppliers = []) {
  const gridState = apiRef?.current?.state;

  if (
    gridState?.sorting?.sortedRows &&
    gridState?.rows?.dataRowIdToModelLookup &&
    gridState?.rows?.tree
  ) {
    return gridSortedRowEntriesSelector(apiRef).map(({ model }) =>
      prepareSupplierExportRow(model)
    );
  }

  return fallbackSuppliers.map(prepareSupplierExportRow);
}

export async function exportStrategyToCSV(
  e,
  strategyInformation,
  hasUSScope,
  userInfoMap,
  allMGCSpend,
  strategicSupplierDataGridApiRef,
  nonStrategicSupplierDataGridApiRef
) {
  e.preventDefault();
  const XLSX = await import('xlsx-js-style');

  //Base Strategy Data
  let strategyData = [
    ['STRATEGY DETAILS #' + strategyInformation.strategyId],
    [
      'The information in this document should be treated as HIGHLY CONFIDENTIAL'
    ],
    [],
    [
      'Strategy Owner',
      userInfoMap
        ? getFormattedUserNameWithId(
            userInfoMap.get(strategyInformation.ownerId),
            strategyInformation.ownerId
          )
        : strategyInformation.ownerId
    ],
    [
      'Strategy Approver',
      userInfoMap
        ? getFormattedUserNameWithId(
            userInfoMap.get(strategyInformation.approverId),
            strategyInformation.approverId
          )
        : strategyInformation.approverId
    ],
    ['Status', strategyInformation.status],
    [
      'Description',
      strategyInformation.strategyDesc
        ? strategyInformation.strategyDesc
        : 'No description provided'
    ]
  ];

  //Append MGC Data
  let mgc = [];
  strategyInformation.decorated.mgcs &&
    strategyInformation.decorated.mgcs.forEach((item, index) => {
      if (index === 0) {
        mgc = ['Material Group Code(s)', item];
      } else {
        mgc = ['', item];
      }
      strategyData.push(mgc);
    });

  //Append Scope Data
  if (isScopeAnyGlobalType(strategyInformation.scope.level)) {
    strategyData.push(['Scope', strategyInformation.scope.level]);
  } else {
    const list = getSortedUnitGroupOrUnitScope(strategyInformation);

    list &&
      list.forEach((item, index) =>
        strategyData.push([index === 0 ? 'Scope' : '', item])
      );
  }

  //Append SDF Data
  let sdf =
    strategyInformation.sdsFormId > 0
      ? [SMALL_BUSINESS_FORM, strategyInformation.sdsFormId.toString()]
      : hasUSScope
        ? [SMALL_BUSINESS_FORM, NO_SBF]
        : '';
  strategyData.push(sdf);

  //create the new worksheet with strategy Data
  let ws = XLSX.utils.aoa_to_sheet(strategyData);

  //Define the styles
  const confidentialStyle = {
    font: { bold: true, name: 'Verdana', sz: 11, color: { rgb: 'FF0000' } }
  };
  const commonStyle = { font: { name: 'Verdana', sz: 11 } };
  const boldStyle = { font: { bold: true, name: 'Verdana', sz: 11 } };
  const alignmentStyle = {
    font: { name: 'Verdana', sz: 11 },
    alignment: { horizontal: 'right' }
  };
  const tableHeaderStyle = {
    font: { bold: true, name: 'Arial', sz: 10, color: { rgb: 'FFFFFF' } },
    alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
    fill: {
      patternType: 'solid',
      fgColor: { rgb: '367C2B' },
      bgColor: { rgb: '367C2B' }
    },
    border: {
      top: { style: 'medium', color: { rgb: '808080' } },
      bottom: { style: 'medium', color: { rgb: '808080' } },
      left: { style: 'medium', color: { rgb: '808080' } },
      right: { style: 'medium', color: { rgb: '808080' } }
    }
  };
  const tableDataStyle = {
    font: { name: 'Verdana', sz: 11 },
    alignment: { wrapText: true }
  };

  //Apply the styles
  for (let i in ws) {
    let cell = XLSX.utils.decode_cell(i);

    if (cell.c === 0) {
      ws[i].s = boldStyle;
    }

    if (cell.c === 1) {
      ws[i].s = commonStyle;
    }

    if (cell.r === 1) {
      ws[i].s = confidentialStyle;
    }
  }

  //Get Last Row of the sheet
  var lastRow = XLSX.utils.decode_range(ws['!ref']).e.r;

  //Basic Spend Data
  let spendData = [
    [],
    [],
    ['Rolling 12 Month Invoice Spend', '', '', 'Forecasted 52 Week Spend', ''],
    [
      'This Strategy',
      formatSpendValue(allMGCSpend, 0, 'this strategy'),
      '',
      'This Strategy',
      formatSpendValue(allMGCSpend, 1, 'this strategy')
    ],
    [
      'All Other Strategies',
      formatSpendValue(allMGCSpend, 0, 'other strategy'),
      '',
      'All Other Strategies',
      formatSpendValue(allMGCSpend, 1, 'other strategy')
    ],
    [
      'Total Strategy Invoice Spend',
      formatAmountWithCommas(getSpendTotal(allMGCSpend, 0), true),
      '',
      'Total Strategy Forecasted Spend',
      formatAmountWithCommas(getSpendTotal(allMGCSpend, 1), true)
    ],
    [],
    ['Amounts are shown in US Dollars (USD).']
  ];

  //Add the spend data to the worksheet
  XLSX.utils.sheet_add_aoa(ws, spendData, { origin: -1 });

  //Apply the Styles
  for (let i in ws) {
    let cell = XLSX.utils.decode_cell(i);

    if (cell.r === lastRow + 3) {
      ws[i].s = boldStyle;
    }

    if (cell.r > lastRow + 3) {
      ws[i].s = commonStyle;
    }

    if (cell.r > lastRow + 3 && (cell.c === 1 || cell.c === 4)) {
      ws[i].s = alignmentStyle;
    }
  }

  //Get Last Row of the sheet
  lastRow = XLSX.utils.decode_range(ws['!ref']).e.r;

  //Supplier Table Headers
  let supplierHeader = [
    [],
    [],
    [
      'Supplier Number',
      'Supplier Name',
      'Status',
      'Rolling 12 Month Spend',
      'Forecasted 52 Week Spend',
      'Forecast %',
      'Risk Rating',
      'SD Indicators',
      'AE Rating',
      'SBM from SRO',
      'Comments'
    ]
  ];

  //Add Supplier Header to the worksheet
  XLSX.utils.sheet_add_aoa(ws, supplierHeader, { origin: -1 });

  let unitHeader = [
    [],
    [
      '',
      'Unit Group',
      'Status',
      'Rolling 12 Month Spend',
      'Forecasted 52 Week Spend'
    ]
  ];

  //supplier data

  for (const value of getSortedSupplierRows(
    strategicSupplierDataGridApiRef,
    strategyInformation.suppliers
  )) {
    let unitData = [];

    XLSX.utils.sheet_add_json(ws, [value], {
      skipHeader: true,
      origin: -1
    });
    XLSX.utils.sheet_add_aoa(ws, unitHeader, { origin: -1 });
    value.unitGroups.forEach((item) => {
      let obj = {};

      Object.assign(obj, {
        empty: '',
        unitGroup: item.hierarchy[1] ? item.hierarchy[1] : item.hierarchy[0],
        status: item.status,
        invoice: Math.round(item.r12Spend ?? 0),
        forecast: Math.round(item.r12Forecast ?? 0)
      });
      unitData.push(obj);
    });
    XLSX.utils.sheet_add_json(ws, unitData, {
      skipHeader: true,
      origin: -1
    });
  }

  for (const value of getSortedSupplierRows(
    nonStrategicSupplierDataGridApiRef,
    strategyInformation.nonStrategicSuppliers
  )) {
    XLSX.utils.sheet_add_json(ws, [value], {
      skipHeader: true,
      origin: -1
    });
  }

  //Apply the Styles
  for (let i in ws) {
    let cell = XLSX.utils.decode_cell(i);

    if (cell.r === lastRow + 3) {
      ws[i].s = tableHeaderStyle;
    }

    if (cell.r > lastRow + 3) {
      ws[i].s = tableDataStyle;
    }

    if (cell.r > lastRow + 3 && cell.c === 9) {
      ws[i].s = commonStyle;
    }
  }

  //Apply the widths for the column
  ws['!cols'] = [
    { wch: 40 },
    { wch: 20 },
    { wch: 20 },
    { wch: 40 },
    { wch: 20 },
    { wch: 20 },
    { wch: 20 },
    { wch: 20 },
    { wch: 20 },
    { wch: 30 }
  ];

  //create the new workbook and write the worksheet to the workbook
  let wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Sheet');

  //write the workbook to the client browser
  XLSX.writeFile(wb, 'StrategyDetails.xlsx');
}

export function canEditTheStrategy(
  strategy,
  loggedInUser,
  isAdmin,
  hasCreateAccess
) {
  return (
    isAdmin ||
    (hasCreateAccess &&
      (ignoreCaseSearch(strategy.ownerId, loggedInUser) === 0 ||
        ignoreCaseSearch(strategy.approverId, loggedInUser) === 0))
  );
}

export function getLinkUri(strategy, loggedInUser, isAdmin, hasCreateAccess) {
  let uri;

  if (canEditTheStrategy(strategy, loggedInUser, isAdmin, hasCreateAccess)) {
    if (strategy.isSetupComplete) uri = '/editStrategy/';
    else uri = '/createStrategy/';
  } else uri = '/viewStrategy/';

  return uri + strategy.strategyId;
}
